import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, Layers, Package,
  Settings, ChevronLeft, ChevronRight,
  CalendarHeart, Users, Building2, FileText,
  ShoppingCart, Truck, DollarSign, ChefHat, Globe,
  // ClipboardList, TrendingUp, Megaphone, ShoppingBag, PackagePlus — only used by
  // the temporarily-hidden nav items below; re-add when those come back.
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { useCateringPendingUpdatesCount } from '@/hooks/useCatering'
import { Avatar } from '@/components/ui/Avatar'
import type { Role } from '@/types/app'

interface NavItem {
  label: string
  href: string
  icon: React.ReactNode
  roles: Role[]
  badgeKey?: 'cateringUpdates'
}

interface NavGroup {
  label: string | null   // null = no section header (top-level items)
  items: NavItem[]
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: null,
    items: [
      { label: 'Dashboard', href: '/dashboard', icon: <LayoutDashboard size={18} />, roles: ['owner','gm','ops','chef','marketing'] },
    ],
  },
  {
    label: 'Products',
    items: [
      { label: 'Products',     href: '/products',       icon: <Layers size={18} />,     roles: ['owner','gm','ops','chef','marketing'] },
      // Temporarily hidden — not deleted. Re-enable by uncommenting.
      // { label: 'Stock Update', href: '/products/stock', icon: <PackagePlus size={18} />, roles: ['owner','gm','ops','chef'] },
      { label: 'Recipes',      href: '/products/recipes', icon: <ChefHat size={18} />,   roles: ['owner','gm','ops','chef'] },
      { label: 'Inventory',    href: '/inventory',      icon: <Package size={18} />,     roles: ['owner','gm','ops','chef'] },
    ],
  },
  {
    label: 'Catering',
    items: [
      { label: 'Orders & Inquiries', href: '/catering',         icon: <CalendarHeart size={18} />, roles: ['owner','gm','ops','marketing'], badgeKey: 'cateringUpdates' },
      { label: 'Catering Pricing',   href: '/catering/pricing', icon: <DollarSign size={18} />,    roles: ['owner','gm','ops'] },
    ],
  },
  {
    label: 'Web Store',
    items: [
      { label: 'Web Orders', href: '/web-orders', icon: <Globe size={18} />, roles: ['owner','gm','ops','marketing'] },
    ],
  },
  {
    label: 'Operations',
    items: [
      // Temporarily hidden — not deleted. Re-enable by uncommenting.
      // { label: 'Operations',  href: '/operations',  icon: <ClipboardList size={18} />, roles: ['owner','gm','ops','chef'] },
      // { label: 'Store Supply', href: '/store-supply', icon: <ShoppingBag size={18} />, roles: ['owner','gm','ops','chef'] },
    ],
  },
  {
    label: 'Wholesale',
    items: [
      { label: 'Buyers',          href: '/buyers',          icon: <Building2 size={18} />,    roles: ['owner','gm','ops'] },
      { label: 'Invoices',        href: '/invoices',        icon: <FileText size={18} />,      roles: ['owner','ops'] },
      { label: 'Purchase Orders', href: '/purchase-orders', icon: <ShoppingCart size={18} />,  roles: ['owner','gm','ops'] },
      { label: 'Suppliers',       href: '/suppliers',       icon: <Truck size={18} />,         roles: ['owner','gm','ops'] },
    ],
  },
  {
    label: 'Manage',
    items: [
      // Temporarily hidden — not deleted. Re-enable by uncommenting.
      // { label: 'Sales',     href: '/sales',     icon: <TrendingUp size={18} />, roles: ['owner','gm'] },
      // { label: 'Marketing', href: '/marketing', icon: <Megaphone size={18} />,  roles: ['owner','gm','marketing'] },
      { label: 'Team',      href: '/team',      icon: <Users size={18} />,      roles: ['owner','gm'] },
      { label: 'Settings',  href: '/settings',  icon: <Settings size={18} />,   roles: ['owner','gm'] },
    ],
  },
]

export function Sidebar() {
  const { user, restaurant } = useAuthStore()
  const { sidebarCollapsed, toggleSidebar } = useUIStore()
  const location = useLocation()
  const { data: cateringUpdates = 0 } = useCateringPendingUpdatesCount()

  const badgeCounts: Partial<Record<NonNullable<NavItem['badgeKey']>, number>> = {
    cateringUpdates,
  }

  const role = user?.role

  // Filter each group to only show items the user can access
  const visibleGroups = NAV_GROUPS
    .map(group => ({
      ...group,
      items: group.items.filter(item => role && item.roles.includes(role)),
    }))
    .filter(group => group.items.length > 0)

  function isActive(href: string) {
    if (location.pathname !== href && !location.pathname.startsWith(href + '/')) return false
    // Yield to a more-specific sibling nav item that also matches the current path
    const moreSpecific = NAV_GROUPS.flatMap(g => g.items).find(
      item => item.href !== href &&
              item.href.startsWith(href + '/') &&
              (location.pathname === item.href || location.pathname.startsWith(item.href + '/'))
    )
    return !moreSpecific
  }

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-30 flex flex-col bg-white border-r border-stone-200 transition-all duration-200',
        sidebarCollapsed ? 'w-15' : 'w-60'
      )}
    >
      {/* Brand header */}
      <div className={cn(
        'flex h-14 items-center border-b border-stone-200 px-3 shrink-0',
        sidebarCollapsed ? 'justify-center' : 'gap-2.5'
      )}>
        {/* Logo or monogram */}
        {restaurant?.logo_url ? (
          <img
            src={restaurant.logo_url}
            alt={restaurant.name}
            className="h-8 w-8 rounded-lg object-cover shrink-0"
          />
        ) : (
          <div
            className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 text-white font-bold text-sm"
            style={{ backgroundColor: restaurant?.brand_colour_primary ?? '#6366f1' }}
          >
            {(restaurant?.name ?? 'R').charAt(0).toUpperCase()}
          </div>
        )}
        {!sidebarCollapsed && restaurant && (
          <span className="font-semibold text-stone-900 text-sm tracking-tight truncate">
            {restaurant.name}
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-2 px-2 scroll-y">
        {visibleGroups.map((group, gi) => (
          <div key={gi} className={cn('mb-1', gi > 0 && 'mt-1')}>

            {/* Section divider + label */}
            {group.label && (
              sidebarCollapsed ? (
                <div className="mx-1 my-1.5 border-t border-stone-100" />
              ) : (
                <div className="px-2.5 pt-3 pb-1">
                  <p className="text-2xs font-semibold uppercase tracking-widest text-stone-400">
                    {group.label}
                  </p>
                </div>
              )
            )}

            {/* Items */}
            <div className="space-y-0.5">
              {group.items.map(item => {
                const active = isActive(item.href)
                const badge = item.badgeKey ? badgeCounts[item.badgeKey] : undefined
                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    className={cn(
                      'flex items-center gap-3 rounded px-2.5 py-2 text-sm font-medium transition-colors relative',
                      active
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900',
                      sidebarCollapsed && 'justify-center px-2'
                    )}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    <span className={cn('shrink-0 relative', active ? 'text-brand-600' : 'text-stone-400')}>
                      {item.icon}
                      {!!badge && sidebarCollapsed && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-brand-500 ring-2 ring-white" />
                      )}
                    </span>
                    {!sidebarCollapsed && (
                      <span className="flex-1 flex items-center justify-between">
                        {item.label}
                        {!!badge && (
                          <span className="ml-2 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-500 text-white text-2xs font-semibold flex items-center justify-center">
                            {badge}
                          </span>
                        )}
                      </span>
                    )}
                  </NavLink>
                )
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User footer */}
      <div className={cn(
        'border-t border-stone-200 p-3 flex items-center gap-3',
        sidebarCollapsed && 'justify-center'
      )}>
        {user && <Avatar name={user.full_name} size="sm" />}
        {!sidebarCollapsed && user && (
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-stone-800 truncate">{user.full_name}</p>
            <p className="text-2xs text-stone-400 capitalize">{user.role}</p>
          </div>
        )}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={toggleSidebar}
        className="absolute -right-3 top-16 h-6 w-6 rounded-full bg-white border border-stone-200 flex items-center justify-center shadow-sm text-stone-500 hover:text-stone-700 hover:border-stone-300 transition-colors"
        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {sidebarCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  )
}
